"use client";

import { ComponentType, FC, useEffect, useMemo, useState } from "react";
import OpenDematButton from "./DematButton";
import Conditional from "@repo/shared-ui/Conditional";
import BFLModal from "@repo/shared-ui/BFLModal";
import dynamic from "next/dynamic";
import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import { DematOtpSendModalProps } from "./DematOtpSendModal";
import {
  useAppSelector,
  selectIsAuthSuccess,
  selectIsAuthSuccessOrPerpetual,
  selectLoginStatus,
  selectLeadStatus,
} from "@repo/web-store";
import {
  handleDematAccountDL,
  handleClickDL,
  handleViewDL,
} from "./DematAccountFunction";
import {
  authenticationConsentCrossIconCtaText,
  authenticationConsentCrossIconInteraction,
} from "../../pages/News/newsTrackingConstants";
import {
  trackAuthenticationConsentDrawerClick,
  trackDematCtaGroupEvent,
} from "./unifiedCt";
import { consentInsert, consentRead } from "@repo/sso/consentApi";
import { getSessionData } from "@repo/shared-utils/common-utils";
import { redirectWithUtmParams } from "./DematUtils";
import { ssoRedirect } from "@repo/sso/redirect-utils";
import { ICompany } from "../News/components/CompanyTag";
import CompanyBuySellModal from "./CompanyBuySellModal";
import useModalCloseOnBrowserButton from "@repo/shared-ui/useModalCloseOnBrowserButton";

const DematConsentModal: ComponentType = dynamic(
  () => import("./DematConsentModal.tsx") as any,
  { ssr: false, loading: () => <BFLSkeleton count={1} height={100} /> },
);
const DematOtpSendModal: ComponentType<DematOtpSendModalProps> = dynamic(
  () => import("./DematOtpSendModal.tsx").then((m) => m.default),
  { ssr: false, loading: () => <BFLSkeleton count={1} height={100} /> },
);
const DematOtpVerifyModal: ComponentType<DematOtpVerifyModalProps> = dynamic(
  () => import("./DematOtpVerifyModal.tsx") as any,
  { ssr: false, loading: () => <BFLSkeleton count={1} height={100} /> },
);
interface DematOtpVerifyModalProps {
  onValidationSuccess?: (otpValue: string) => void;
  onValidationFailure?: (otpValue: string) => void;
  mobileNumber?: string;
}

interface DematAccountProps {
  openDematButtonText?: string;
  resumeJourneyText?: string;
  compName: string;
  exchId: string;
  formattedSymbol: string;
  nseToken: number;
  bseToken: number;
  isFloating?: boolean;
  showArrow?: boolean;
  showTradeNow?: boolean;
  tradeNowText?: string;
  showBuySellButtons?: boolean;
  buyText?: string;
  sellText?: string;
  headerName?: string;
  companies?: ICompany[];
  modalTitle?: string;
  onMasterCtaClick?: (ctaText: string) => void;
  showUnifiedCt?: boolean;
  showCtaPadding?: boolean;
}

export const handleSSORedirect = async (
  sectionTitle: string,
  loginStatus: string,
  exchId: string,
  formattedSymbol: string,
  nseToken: number,
  bseToken: number,
  headerName: string = "Bajaj Broking",
) => {
  const { mobileNo } = (await getSessionData()) || {};
  const ssoParams = {
    buySellRedirectUrl: process.env.NEXT_PUBLIC_BFSL_DOMAIN,
    tokenExchId: exchId,
    sectionTitle: sectionTitle.toLocaleUpperCase() ?? "",
    headerName: headerName,
  };

  return ssoRedirect(
    {
      href: ssoParams?.buySellRedirectUrl ?? "",
      data: {
        screen: "orderpad",
        token: exchId === "0" ? nseToken : bseToken,
        symbol: formattedSymbol,
        action: sectionTitle,
        mobileNo: mobileNo,
        headerName: headerName,
      },
      target: "",
      checkCart: false,
      showResumePopup: false,
    },
    loginStatus,
  );
};

export const handleTradeNowRedirect = async (
  loginStatus: string,
  headerName: string = "Bajaj Broking",
) => {
  const { mobileNo } = (await getSessionData()) || {};
  const ssoParams = {
    buySellRedirectUrl: process.env.NEXT_PUBLIC_BFSL_DOMAIN,
    headerName: headerName,
  };

  return ssoRedirect(
    {
      href: ssoParams?.buySellRedirectUrl ?? "",
      data: {
        screen: "dashboard",
        mobileNo: mobileNo,
        headerName: headerName,
      },
      target: "",
      checkCart: false,
      showResumePopup: false,
    },
    loginStatus,
  );
};

const DematAccount: FC<DematAccountProps> = ({
  openDematButtonText,
  resumeJourneyText,
  compName,
  nseToken,
  bseToken,
  exchId,
  formattedSymbol,
  isFloating = false,
  showArrow = true,
  showTradeNow,
  tradeNowText,
  showBuySellButtons,
  buyText,
  sellText,
  headerName = "Bajaj Broking",
  companies = [],
  modalTitle,
  onMasterCtaClick,
  showUnifiedCt = false,
  showCtaPadding,
}) => {
  const [openModal, setOpenModal] = useState<
    "consent" | "send" | "verify" | "none"
  >("none");
  const [consentProvided, setConsentProvided] = useState<boolean>(false);
  const [buttonText, setButtonText] = useState<string | undefined>(
    openDematButtonText,
  );
  const [sessionMobileNo, setSessionMobileNo] = useState<string | undefined>(
    undefined,
  );
  const [openCompanyBuySellModal, setOpenCompanyBuySellModal] =
    useState<boolean>(false);
  const loginStatus = useAppSelector(selectLoginStatus);
  const leadStatus = useAppSelector(selectLeadStatus);
  const isAuthSuccess = useAppSelector(selectIsAuthSuccess);
  const isAuthSuccessOrPerpetual = useAppSelector(
    selectIsAuthSuccessOrPerpetual,
  );
  const handleCloseModal = () => {
    setOpenModal("none");
  };
  useModalCloseOnBrowserButton(openModal !== "none", handleCloseModal);

  // Get mobile number from session data
  useEffect(() => {
    let isMounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const fetchSessionMobileNumber = async (retryCount = 0) => {
      try {
        const { mobileNo } = await getSessionData();
        if (mobileNo) {
          if (isMounted) setSessionMobileNo(mobileNo);
        } else if (retryCount < 10 && isMounted) {
          timeoutId = setTimeout(
            () => fetchSessionMobileNumber(retryCount + 1),
            500,
          );
        }
      } catch (error) {
        if (retryCount < 10 && isMounted) {
          timeoutId = setTimeout(
            () => fetchSessionMobileNumber(retryCount + 1),
            500,
          );
        } else {
          console.error("Error fetching session data:", error);
        }
      }
    };

    fetchSessionMobileNumber();

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const submitConsentInsert = async () => {
    const consentResponse: any = await consentInsert(
      "BFSL",
      "BFL Entity",
      true,
    );
    return consentResponse;
  };

  /**
   * getConsentReadData
   */
  const getConsentReadData = async () => {
    const consentReadData = await consentRead("BFSL", "34", "BFL Entity");
    if (consentReadData?.data) {
      if (consentReadData?.data?.consentValue === "2") {
        setConsentProvided(true);
      }
    }
  };

  useEffect(() => {
    if (leadStatus === null) {
      console.warn("Lead status is null");
      return;
    }
    getConsentReadData();
    if (leadStatus === "Inprogress") {
      setButtonText(resumeJourneyText ?? "RESUME JOURNEY");
    } else if (leadStatus === "ETB" && showTradeNow) {
      setButtonText(tradeNowText ?? "TRADE NOW");
    } else {
      setButtonText(openDematButtonText ?? "OPEN DEMAT ACCOUNT");
    }
  }, [
    leadStatus,
    showTradeNow,
    tradeNowText,
    openDematButtonText,
    resumeJourneyText,
  ]);

  // Handle successful OTP validation
  const handleOtpValidationSuccess = (otpValue: string) => {
    setOpenModal("none");
  };

  // Handle failed OTP validation
  const handleOtpValidationFailure = (otpValue: string) => {
    // TODO - Keep modal open for retry
  };

  // Format mobile number for display
  const formatMobileForDisplay = (mobile: string) => {
    if (!mobile || mobile.length < 4) {
      return "";
    }
    return `${mobile.slice(0, 2)}xxxxxx${mobile.slice(-2)}`;
  };

  const shouldOpenModal = useMemo(() => {
    return companies.length > 1 && companies.length < 4;
  }, [companies]);

  const trackCtaGroupEvent = (
    ctaText: string,
    submissionStatus: string = "",
  ) => {
    void trackDematCtaGroupEvent(headerName, ctaText, submissionStatus);
  };

  return (
    <>
      <Conditional
        if={
          (isAuthSuccessOrPerpetual && leadStatus === null) ||
          loginStatus === "pending"
        }
      >
        <BFLSkeleton width={225} count={1} height={40} />
      </Conditional>

      <Conditional
        if={
          (isAuthSuccessOrPerpetual &&
            (leadStatus === "" ||
              leadStatus === "null" ||
              leadStatus === "Inprogress")) ||
          loginStatus === "failure"
        }
      >
        <OpenDematButton
          showArrow={showArrow}
          openDematButtonText={buttonText}
          onClick={() => {
            const effectiveCtaText =
              buttonText ?? openDematButtonText ?? "OPEN DEMAT ACCOUNT";
            if (showUnifiedCt && onMasterCtaClick) {
              onMasterCtaClick(effectiveCtaText);
            } else {
              handleDematAccountDL(
                loginStatus,
                effectiveCtaText,
                compName ?? "Unknown Company",
              );
            }

            // For non-logged-in users, redirect immediately and return
            if (!isAuthSuccessOrPerpetual) {
              redirectWithUtmParams(
                "https://pwa.bajajfinservsecurities.in/?sso-module=bfsl",
                "false",
              );
              return; // Exit early to prevent modal from opening
            }

            // For logged-in users, determine which modal to show
            const modalToOpen = consentProvided === true ? "send" : "consent";
            setOpenModal(modalToOpen);

            if (modalToOpen === "consent") {
              if (showUnifiedCt) {
                void trackAuthenticationConsentDrawerClick(
                  effectiveCtaText,
                  "consent_modal_opened",
                );
              } else {
                handleViewDL("REDIRECTING TO BFSL");
              }
            }
          }}
        />
      </Conditional>

      <Conditional
        if={
          isAuthSuccess &&
          leadStatus === "ETB" &&
          showTradeNow === false &&
          showBuySellButtons === true
        }
      >
        <div
          className={`flex items-center space-x-2 ${(showCtaPadding ?? true) ? "py-[10px]" : "py-0"} ${isFloating && leadStatus === "ETB" ? "fixed bottom-0 left-0 h-[72px] w-full justify-center bg-white pl-4 pr-4 shadow-[-4px_-4px_6px_#00000014]" : "between-center"}`}
        >
          <button
            className={`cursor-pointer rounded-full bg-[#00A989] px-10 py-2 text-sm font-medium text-white ${isFloating && leadStatus === "ETB" ? "mr-[10px] h-[40px] w-[167px]" : ""}`}
            onClick={() => {
              // Track button click
              if (shouldOpenModal) {
                trackCtaGroupEvent(buyText ?? "BUY");
                setOpenCompanyBuySellModal(true);
                return;
              }
              if (showUnifiedCt) {
                trackCtaGroupEvent(buyText ?? "BUY");
              } else {
                handleClickDL("BUY", formattedSymbol);
              }
              handleSSORedirect(
                "buy",
                loginStatus,
                exchId,
                formattedSymbol,
                nseToken,
                bseToken,
                headerName,
              );
            }}
            data-testid="buy-button"
          >
            {buyText ?? "BUY"}
          </button>
          <button
            className={`cursor-pointer rounded-full bg-[#E85B0F] px-10 py-2 text-sm font-medium text-white ${isFloating && leadStatus === "ETB" ? "h-[40px] w-[167px]" : ""}`}
            onClick={() => {
              // Track button click
              if (shouldOpenModal) {
                trackCtaGroupEvent(sellText ?? "SELL");
                setOpenCompanyBuySellModal(true);
                return;
              }
              if (showUnifiedCt) {
                trackCtaGroupEvent(sellText ?? "SELL");
              } else {
                handleClickDL("SELL", formattedSymbol);
              }
              handleSSORedirect(
                "sell",
                loginStatus,
                exchId,
                formattedSymbol,
                nseToken,
                bseToken,
                headerName,
              );
            }}
            data-testid="sell-button"
          >
            {sellText ?? "SELL"}
          </button>
        </div>
      </Conditional>

      <Conditional
        if={
          isAuthSuccess &&
          leadStatus === "ETB" &&
          showTradeNow === true &&
          showBuySellButtons === false
        }
      >
        <OpenDematButton
          openDematButtonText={buttonText}
          onClick={() => {
            const effectiveCtaText = buttonText ?? tradeNowText ?? "TRADE NOW";
            if (showUnifiedCt && onMasterCtaClick) {
              onMasterCtaClick(effectiveCtaText);
            } else {
              handleDematAccountDL(
                loginStatus,
                effectiveCtaText,
                compName ?? "",
              );
            }
            handleClickDL("TRADE NOW", formattedSymbol);

            handleTradeNowRedirect(loginStatus, headerName);
          }}
        />
      </Conditional>

      {/* Consent Modal */}
      <Conditional if={openModal === "consent"}>
        <BFLModal
          data-testid="bfl-modal"
          isOpen={true}
          onClose={() => {
            setOpenModal("none");
            if (showUnifiedCt) {
              void trackAuthenticationConsentDrawerClick(
                authenticationConsentCrossIconCtaText,
                "",
                authenticationConsentCrossIconInteraction,
              );
            } else {
              handleClickDL("CLOSE", "REDIRECTING TO BFSL");
            }
          }}
          title="Redirecting you to Bajaj Financial Securities Limited"
          secondaryButtonText="Skip"
          onSecondaryButtonClick={() => {
            setOpenModal("none");
            if (showUnifiedCt) {
              void trackAuthenticationConsentDrawerClick("Skip", "skip");
            } else {
              handleClickDL("Skip", "REDIRECTING TO BFSL");
            }
            redirectWithUtmParams(
              "https://pwa.bajajfinservsecurities.in/?sso-module=bfsl",
              "false",
            );
          }}
          onPrimaryButtonClick={async () => {
            try {
              // Call consent insert API
              const consentResponse = await submitConsentInsert();

              // Check if consent was successful
              if (
                consentResponse.statusCode === 90 &&
                consentResponse.status === "success"
              ) {
                setConsentProvided(true);
                // Store mobile number from response for prefilling
                if (consentResponse.data?.mobileNumber) {
                  setSessionMobileNo(consentResponse.data.mobileNumber);
                }
                setOpenModal("send");
                if (showUnifiedCt) {
                  void trackAuthenticationConsentDrawerClick(
                    "Continue",
                    "continue",
                  );
                } else {
                  handleClickDL("Continue", "REDIRECTING TO BFSL");
                }
              } else {
                console.error("Consent API failed:", consentResponse.message);
                setConsentProvided(false);
                // Handle error case - maybe show error message or keep modal open
              }
            } catch (error) {
              // Handle error case
              console.error("Error in consent submission:", error);
              setConsentProvided(false);
            }
          }}
          enableScroll={false}
          buttonText="Continue"
        >
          <DematConsentModal />
        </BFLModal>
      </Conditional>

      {/* Send OTP Modal */}
      <Conditional if={openModal === "send"}>
        <BFLModal
          isOpen={true}
          onClose={() => {
            setOpenModal("none");
            handleClickDL("CLOSE", "OPEN YOUR FREE DEMAT ACCOUNT");
          }}
          onPrimaryButtonClick={() => {
            buttonText === (resumeJourneyText ?? "RESUME JOURNEY")
              ? handleClickDL("resume journey ", "OPEN YOUR FREE DEMAT ACCOUNT")
              : handleClickDL(
                  "open_demat_account",
                  "OPEN YOUR FREE DEMAT ACCOUNT",
                );
            setOpenModal("verify");
          }}
          title="Open your free Demat and trading account"
          enableScroll={false}
          subtitle="Kickstart your investment journey today"
        >
          <DematOtpSendModal
            setOpenModal={setOpenModal}
            prefillMobileNumber={sessionMobileNo}
          />
        </BFLModal>
      </Conditional>

      {/* Verify OTP Modal */}
      <Conditional if={openModal === "verify"}>
        <BFLModal
          isOpen={true}
          onClose={() => {
            setOpenModal("none");
            handleClickDL("CLOSE", "OTP VERIFICATION PAGE");
          }}
          title="Verification required"
          subtitle={
            sessionMobileNo
              ? `A one-time password has been sent to your mobile number +91 ${formatMobileForDisplay(sessionMobileNo)}`
              : "A one-time password has been sent to your registered mobile number"
          }
        >
          <DematOtpVerifyModal
            onValidationSuccess={handleOtpValidationSuccess}
            onValidationFailure={handleOtpValidationFailure}
            mobileNumber={sessionMobileNo}
          />
        </BFLModal>
      </Conditional>

      {/* Company Buy Sell Modal */}
      <Conditional if={openCompanyBuySellModal}>
        <CompanyBuySellModal
          isOpen={openCompanyBuySellModal}
          onClose={() => {
            setOpenCompanyBuySellModal(false);
          }}
          companies={companies}
          headerName={headerName}
          buyText={buyText ?? "BUY"}
          sellText={sellText ?? "SELL"}
          title={modalTitle}
        />
      </Conditional>
    </>
  );
};

export default DematAccount;
